import { useTheme } from '@Theme';
import type * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Noop } from '../../../../extViewmodels/Utils';
import {
	IImpersonationContextComponentProps,
	IPeopleSearchAddedTagsState,
	ImpersonationContextKey,
} from '../../../../models';
import { EmailComposerContext } from '../../../../models/Email';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import { PersistentStorageManager } from '../../../../models/Storage';
import { getDefaultOwnedByContactFilterFilterCriteria } from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useSavedSearchesQuery } from '../../../../queries/ContactSearch';
import {
	ComposeResourceEmailViewModel,
	ContactFilterCriteriaProperty,
	ContactViewModel,
	DefaultBulkSendExcludedFilterCriteria,
	DefaultBulkSendExcludedTags,
	FilterOperator,
	IAccountTag,
	IContact,
	IContactFilterCriteria,
	IContactsFilterRequest,
	IOperationResultNoValue,
	IOwnershipFilter,
	SendEmailFrom,
	TagsViewModel,
	VmUtils,
} from '../../../../viewmodels/AppViewModels';
import { AdvancedFiltersContextProvider } from '../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { AdvancedFiltersLegacyUI } from '../../../components/AdvancedFilters/AdvancedFiltersLegacyUI';
import { Checkbox } from '../../../components/Checkbox';
import { Button } from '../../../components-v2/Button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../../components-v2/Drawer/Drawer';
import { GlobalSearch } from '../../../components/GlobalSearch';
import { BrowseAllTagsFlyout } from '../../../components/entities/tags/BrowseAllTagsFlyout';
import { RadioButton } from '../../../components/RadioButton';
import { ITestEmailResult, TestEmailModal } from '../../../components/email/TestEmailModal';
import { PaperPlaneIcon } from '../../../components/svgs/icons/PaperPlaneIcon';
import { SearchIcon } from '../../../components/svgs/icons/SearchIcon';
import { XIcon } from '../../../components/svgs/icons/XIcon';
import { brandPrimaryHover, navigation } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { PeopleCollapsibleSection } from '../../PeopleV2/PeopleContainer/PeopleCollapsibleSection';
import { ISelectableTag as ITag, reconcileBrowsedTags } from './reconcileBrowsedTags';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	/** Default === true */
	applyOwnedByCriteria?: boolean;
	className?: string;
	hideFooter?: boolean;
	nextCtaText?: React.ReactNode;
	onNextClick?(explicitelyPreventContactFilterRequest?: boolean): void;
	canClearFilters?: boolean;
}

const MaxItemsToShowByDefault = 5;

const SearchBase: React.FC<IProps> = ({
	applyOwnedByCriteria = true,
	className = '',
	impersonationContext,
	nextCtaText,
	onNextClick,
	canClearFilters = true,
}) => {
	const userSession = useUserSession();
	const logger = useEventLogging();
	const [tags, setTags] = useState<ITag[]>([]);
	const [selectedContacts, setSelectedContacts] = useState<ContactViewModel[]>([]);

	const [selectedSavedSearch, setSelectedSavedSearch] = useState<Api.ContactSearch | null>(null);
	const [showingAdvancedFiltersFlyout, setShowingAdvancedFiltersFlyout] = useState(false);
	const [showingBrowseAllTags, setShowingBrowseAllTags] = useState(false);
	const { data: savedSearches = [], isLoading: searchesLoading } = useSavedSearchesQuery({});
	const popularTags = useRef(new TagsViewModel(userSession).impersonate(impersonationContext)).current;
	const { emailComposer, sendTestEmail } = React.useContext(EmailComposerContext) || {};
	const sendFlowGlobalSearchAddedTags = useRef(
		`sendFlowGlobalSearchAddedTags-${impersonationContext?.account?.id ?? userSession?.account?.id}`
	).current;
	const [showingTestEmailModal, setShowingTestEmailModal] = useState(false);
	const { colors } = useTheme();

	useEffect(() => {
		let startingTags: ITag[] = [];
		getStoredTags()
			.then(storedTags => {
				startingTags = storedTags;
				return popularTags.getSuggestedTagsForContactsV2(false, 6);
			})
			.then((result = []) => {
				startingTags = [...result.map(x => ({ selected: false, tag: x.name })), ...startingTags].filter(
					x => !startingTags.includes(x)
				);
				setTags(startingTags);
			})
			.catch((err: IOperationResultNoValue) => {
				logger.logApiError('LoadSuggestedTags-Error', err);
			});

		// clear any previously selected tags from criteria
		// this is to handle case when user selects tags, then
		// moves to the next screen, uses the advanced tag filter,
		// and then comes back to the first screen.
		const contactFilterRequest: IContactsFilterRequest = { criteria: [] };

		if (emailComposer?.emailMessage && !emailComposer?.isFollowUp && canClearFilters) {
			setSelectedContacts(
				emailComposer.emailMessage.contactsToAdd.filter(c => {
					return !emailComposer.emailMessage.contactsToOmit.getById(c.id);
				})
			);

			emailComposer.emailMessage.contactsFilterRequest = {
				contactFilterRequest,
				ownershipFilter: emailComposer.emailMessage.contactsFilterRequest?.ownershipFilter,
			};
			emailComposer.setAllowContactFilterTagSearchChangesOverride(true);
		}

		emailComposer?.resetRecipientsResultsList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const sorted = VmUtils.sortContactFilterCriteria(
			emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest
		);
		const selectedTags = tags.filter(t => t.selected);

		let contactFilterRequest: IContactsFilterRequest = null;
		let ownershipFilter: IOwnershipFilter = null;

		if (selectedSavedSearch) {
			const notEmptyCriteria = selectedSavedSearch.filter?.criteria?.filter(
				x => x.criteria?.length || x.property != null
			);

			contactFilterRequest = {
				criteria: [...notEmptyCriteria, ...DefaultBulkSendExcludedFilterCriteria],
			};
			if (selectedSavedSearch.ownershipFilter) {
				ownershipFilter = selectedSavedSearch.ownershipFilter;
			}
		} else {
			if (!emailComposer?.isFollowUp) {
				if (tags.filter(t => t.selected).length > 0) {
					ownershipFilter =
						!impersonationContext &&
						applyOwnedByCriteria &&
						!emailComposer?.emailMessage?.isSendFromOption(SendEmailFrom.ContactOwner)
							? (emailComposer?.emailMessage?.contactsFilterRequest.ownershipFilter ??
								getDefaultOwnedByContactFilterFilterCriteria(
									userSession,
									emailComposer?.emailMessage?.options?.sendEmailFromUserId ||
										emailComposer?.emailMessage?.options?.sendEmailFromUser?.id
								))
							: emailComposer?.emailMessage?.contactsFilterRequest.ownershipFilter;
					contactFilterRequest = {
						criteria: VmUtils.concatContactFilterCriteria(
							[
								{
									criteria: [
										...selectedTags.map(tag => {
											return {
												property: ContactFilterCriteriaProperty.Tag,
												value: tag.tag,
											};
										}),
									],
									op: FilterOperator.Or,
								} as IContactFilterCriteria,
								...sorted.filters.filter(x => x.property !== ContactFilterCriteriaProperty.OwnedBy),
							].filter(x => !!x),
							Array.from(DefaultBulkSendExcludedTags).map(x => ({
								op: FilterOperator.Not,
								property: ContactFilterCriteriaProperty.Tag,
								value: x,
							}))
						),
					};
				} else {
					// reset to defaults
					if (emailComposer?.emailMessage?.contactsToAdd.length > 0) {
						// By setting ownershipFilter to undefined we will not send the ownership filter in the API request.
						// The check in `TagEmailTagList` checks if the ownershipFilter `!== null` which will be true if we set it to undefined.
						// That means we send nothing in the request which ensures we only get the manually selected contacts.
						emailComposer.emailMessage.contactsFilterRequest.ownershipFilter = undefined;
					}
				}
			}
		}
		emailComposer.emailMessage.contactsFilterRequest =
			emailComposer?.emailMessage && !!contactFilterRequest
				? {
						...(emailComposer.emailMessage.contactsFilterRequest ?? {}),
						contactFilterRequest,
						ownershipFilter,
					}
				: {
						...(emailComposer.emailMessage.contactsFilterRequest ?? {}),
						contactFilterRequest,
					};
		emailComposer?.resetRecipientsResultsList();
	}, [applyOwnedByCriteria, emailComposer, impersonationContext, selectedSavedSearch, tags, userSession]);

	const addToPersistentUserSelectedTags = React.useCallback((...newTags: string[]) => {
		PersistentStorageManager.local
			.getObject<IPeopleSearchAddedTagsState>(sendFlowGlobalSearchAddedTags)
			.then(storedPeopleTagsState => {
				const userStoredTags = storedPeopleTagsState?.tags ?? [];
				const persistentTags = Array.from(new Set([...userStoredTags, ...newTags]));
				PersistentStorageManager.local.setObject<IPeopleSearchAddedTagsState>(sendFlowGlobalSearchAddedTags, {
					tags: persistentTags,
				});
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isForResource = React.useCallback(() => {
		return (emailComposer as ComposeResourceEmailViewModel)?.resourceSelector;
	}, [emailComposer]);

	const getApproximation = React.useCallback(() => {
		if (!emailComposer?.emailMessage || emailComposer?.isFollowUp || isForResource()) {
			return;
		}
		const promise = emailComposer.emailMessage.getEmailApproximation();
		promise?.catch((error: IOperationResultNoValue) => {
			logger.logApiError('HasEmailApproximationLoad-Error', error);
		});
	}, [emailComposer, logger, isForResource]);

	const getStoredTags = React.useCallback(() => {
		return PersistentStorageManager.local
			.getObject<IPeopleSearchAddedTagsState>(sendFlowGlobalSearchAddedTags)
			.then(storedTags => {
				const userStoredTags = storedTags?.tags ?? [];
				if (userStoredTags.length === 0) {
					PersistentStorageManager.local.remove(sendFlowGlobalSearchAddedTags);
				}

				return userStoredTags.map(ust => ({ selected: false, tag: ust }));
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tags]);

	const onContactSelected = React.useCallback(
		async (contactModel: IContact) => {
			const contact = new ContactViewModel(userSession, contactModel);

			if (emailComposer.emailMessage.contactsToOmit.has(contact)) {
				emailComposer.emailMessage.contactsToOmit.removeItems([contact]);
			}

			emailComposer.emailMessage.contactsToAdd.splice(0, 0, contact);

			if (!emailComposer?.isFollowUp) {
				setSelectedContacts(
					emailComposer.emailMessage.contactsToAdd.filter(c => !emailComposer.emailMessage.contactsToOmit.getById(c.id))
				);
			}

			if (emailComposer.emailMessage.options?.sendEmailFrom === SendEmailFrom.ContactOwner) {
				emailComposer.emailMessage.options = {
					...emailComposer.emailMessage.options,
					sendEmailFrom: SendEmailFrom.CurrentUser,
				};
			}

			getApproximation();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[emailComposer, isForResource, getApproximation]
	);

	const onPreviewRecipientsClick = () => {
		onNextClick(tags.filter(t => t.selected).length === 0 && selectedContacts.length > 0);
	};

	const onRemoveContactClick = (contact: ContactViewModel) => () => {
		emailComposer.emailMessage.contactsToAdd.removeItems([contact]);
		setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id));
		getApproximation();
	};

	const onSavedSearchClick = (search: Api.ContactSearch) => () => {
		setSelectedSavedSearch(search);
		setTags(tags.map(t => ({ selected: false, tag: t.tag })));
	};

	const onSelectedTagsChanged = React.useCallback(
		(newTag: string) => {
			const updatedTags = tags.filter(t => t.tag !== newTag);
			updatedTags.unshift({ selected: true, tag: newTag });
			setTags(updatedTags);
		},
		[tags]
	);

	const onSendTestClick = async () => {
		setShowingTestEmailModal(true);
	};

	const onTagClick = (t: string) => async () => {
		let updatedTags = [...tags];
		const existingTag = updatedTags.find(ut => ut.tag === t);
		if (existingTag) {
			updatedTags = updatedTags.map(ut => {
				if (ut.tag === t) {
					ut.selected = !ut.selected;
				}
				return ut;
			});
		} else {
			updatedTags.push({ selected: true, tag: t });
		}

		if (updatedTags.some(tag => tag.selected)) {
			setSelectedSavedSearch(null);
			onSelectedTagsChanged(t);
		}

		setTags(updatedTags);
	};

	const onTagSelected = React.useCallback(
		(tag: IAccountTag) => {
			// clear selected saved searches from the existing criteria because cannot include saved search
			// when also using individual tags.

			setSelectedSavedSearch(null);

			onSelectedTagsChanged(tag.name);

			addToPersistentUserSelectedTags(tag.name);
		},
		[onSelectedTagsChanged, addToPersistentUserSelectedTags]
	);

	const onBrowseAllTagsApply = (selectedTags: IAccountTag[]) => {
		const existingTagNames = new Set(tags.map(t => t.tag));
		const newTagNames = selectedTags.map(t => t.name).filter(name => !existingTagNames.has(name));
		if (newTagNames.length > 0) {
			addToPersistentUserSelectedTags(...newTagNames);
		}
		if (selectedTags.length > 0) {
			setSelectedSavedSearch(null);
		}
		setTags(reconcileBrowsedTags(tags, selectedTags));
		setShowingBrowseAllTags(false);
	};

	const onAdvancedFiltersFlyoutCancel = () => {
		setShowingAdvancedFiltersFlyout(false);
	};

	const onAdvancedFiltersFlyoutSave = (
		filterCriteria: IContactFilterCriteria[],
		householdGrouping?: boolean,
		ownershipFilter?: IOwnershipFilter
	) => {
		if (!emailComposer?.emailMessage) {
			return;
		}

		// Clear selected tags and saved search since we're using advanced filters
		setTags(tags.map(t => ({ ...t, selected: false })));
		setSelectedSavedSearch(null);

		// Apply the filter criteria from the CompoundFiltersPanel
		emailComposer.emailMessage.contactsFilterRequest = {
			contactFilterRequest: {
				criteria:
					filterCriteria?.length > 0
						? VmUtils.concatContactFilterCriteria(
								filterCriteria,
								Array.from(DefaultBulkSendExcludedTags).map(x => ({
									op: FilterOperator.Not,
									property: ContactFilterCriteriaProperty.Tag,
									value: x,
								}))
							)
						: [],
			},
			ownershipFilter,
		};

		if (householdGrouping !== undefined) {
			emailComposer.emailMessage.groupByHousehold = householdGrouping;
		}

		emailComposer.resetRecipientsResultsList();
		setShowingAdvancedFiltersFlyout(false);

		// Advance to the preview recipients step
		onNextClick?.();
	};

	const renderContacts = () => {
		return selectedContacts.map((c, i) => (
			<div className={css(styleSheet.flex, styleSheet.itemContainer, styleSheet.contactContainer)} key={i}>
				<button className={css(styleSheet.removeContactButton)} onClick={onRemoveContactClick(c)}>
					<XIcon height={12} width={12} color={navigation} />
				</button>
				<div className={`contact-content ${css(styleSheet.contentContainer)}`}>
					<span>{getDisplayName(c)}</span>
					<span className={css(styleSheet.smallGray)}>{c.jobTitle ?? ''}</span>
				</div>
			</div>
		));
	};

	const renderSavedSearches = () => {
		// Do not allow Levitate admin users to view saved searches of users
		// There is no api allowed for fetching them with impersonation
		if (searchesLoading || impersonationContext?.isValid) {
			return [];
		}

		return savedSearches.map((s, i) => {
			const isSelected = s.id === selectedSavedSearch?.id;
			return (
				<div className={css(styleSheet.itemContainer)} key={s.id}>
					<RadioButton
						checked={isSelected}
						key={s.id}
						id={`saved-searches-${i}`}
						name='saved-searches'
						onChange={Noop}
						onClick={onSavedSearchClick(s)}
						readOnly={true}
					>
						<div className={css(styleSheet.contentContainer)}>
							<span
								className={`saved-search ${css(
									styleSheet.pill,
									isSelected ? styleSheet.pillSelected : styleSheet.pillUnselected
								)}`}
							>
								<SearchIcon fillColor={isSelected ? '#fff' : brandPrimaryHover} />
								{s.name}
							</span>
						</div>
					</RadioButton>
				</div>
			);
		});
	};

	const renderTags = () => {
		return tags.map(({ tag, selected }, i) => {
			const isSelected = selected;
			return (
				<div className={css(styleSheet.flex, styleSheet.itemContainer)} key={i}>
					<Checkbox checked={isSelected} id={`${i}`} onChange={onTagClick(tag)} />
					<div className={css(styleSheet.contentContainer)} onClick={onTagClick(tag)}>
						<span
							className={`tag ${css(
								styleSheet.pill,
								isSelected ? styleSheet.pillSelected : styleSheet.pillUnselected
							)}`}
						>
							{tag}
						</span>
					</div>
				</div>
			);
		});
	};

	const canBrowseAllTags = !emailComposer?.isFollowUp;

	const onTestEmailModalClose = (result?: ITestEmailResult, canceled?: boolean) => {
		setShowingTestEmailModal(false);
		if (result && result.success && !canceled) {
			toast.success('Test email sent successfully!');
		}
	};
	return (
		<div
			key={impersonationContext?.isValid ? impersonationContext.user?.id : 'tags'}
			className={`${css(styleSheet.tags)} ${className}`}
		>
			<div className={css(styleSheet.tagsInner)}>
				<div className={css(styleSheet.tagsHeader)}>Select Recipients to Send:</div>
				{(!!sendTestEmail || !emailComposer?.isFollowUp) && (
					<div className={css(styleSheet.sendATestWrapper)}>
						{!!sendTestEmail && (
							<>
								<TestEmailModal
									isOpen={showingTestEmailModal}
									onRequestClose={onTestEmailModalClose}
									onSendTestEmail={sendTestEmail}
								/>
								<button className={css(styleSheet.sendATestButton)} onClick={onSendTestClick}>
									<PaperPlaneIcon style={{ color: colors.primary.main }} />
									<span>Send a Test</span>
								</button>
							</>
						)}
						{!emailComposer?.isFollowUp && (
							<>
								<button className={css(baseStyleSheet.brandLink)} onClick={() => setShowingAdvancedFiltersFlyout(true)}>
									<div>Advanced Filters</div>
								</button>
								<Drawer
									open={showingAdvancedFiltersFlyout}
									onOpenChange={open => !open && onAdvancedFiltersFlyoutCancel()}
									swipeDirection='right'
								>
									<DrawerContent>
										<DrawerHeader>
											<DrawerTitle>Advanced Filters</DrawerTitle>
										</DrawerHeader>
										<AdvancedFiltersContextProvider
											initialFilterCriteria={
												emailComposer?.emailMessage?.contactsFilterRequest?.contactFilterRequest?.criteria || []
											}
											initialOwnershipFilter={emailComposer?.emailMessage?.contactsFilterRequest?.ownershipFilter}
											initialSelectedUserId={emailComposer?.emailMessage?.options?.sendEmailFromUserId}
											onSave={onAdvancedFiltersFlyoutSave}
											refresh={showingAdvancedFiltersFlyout}
											initialHouseholdGroupingOn={emailComposer?.emailMessage?.groupByHousehold}
											impersonationContext={impersonationContext?.toJs()}
										>
											<AdvancedFiltersLegacyUI
												hideHeader
												onDismissFlyout={onAdvancedFiltersFlyoutCancel}
												showPopularTags
												showSavedSearches
												savedSearchesReadOnly
												showHousehold={userSession.account.features?.households?.enabled}
												showOwnershipFilters
												showStatusFilters
												showLocationFilters
												showJobTitleFilter
												showCompanyTagFilters
												showCustomFields
												showCreateDateFilters
												showLastCommunicationDate
												showKeyFactFilters={!isForResource()}
												impersonationContext={impersonationContext?.toJs()}
											/>
										</AdvancedFiltersContextProvider>
									</DrawerContent>
								</Drawer>
							</>
						)}
					</div>
				)}
				<div className={css(styleSheet.globalSearch)}>
					<GlobalSearch
						onContactSelected={
							emailComposer?.emailMessage?.options.sendEmailFrom !== SendEmailFrom.ContactOwner
								? onContactSelected
								: null
						}
						onTagSelected={onTagSelected}
					/>
				</div>
				{tags.length > 0 || canBrowseAllTags ? (
					<div className={css(styleSheet.sectionContainer)}>
						{tags.length > 0 ? (
							<>
								<div className={css(styleSheet.header)}>Popular Tags</div>
								<div className={css(styleSheet.tagsContainer)}>
									<PeopleCollapsibleSection
										defaultCountToShow={MaxItemsToShowByDefault}
										items={renderTags()}
										itemHeightPx={31}
										showLoader={false}
									/>
								</div>
							</>
						) : null}
						{canBrowseAllTags ? (
							<>
								<Button
									variant='link'
									size='sm'
									styles={[styleSheet.browseAllTagsLink]}
									onClick={() => setShowingBrowseAllTags(true)}
								>
									Browse All Tags
								</Button>
								<BrowseAllTagsFlyout
									impersonationContext={impersonationContext?.toJs()}
									initialSelectedTagNames={tags.filter(t => t.selected).map(t => t.tag)}
									isOpen={showingBrowseAllTags}
									onApply={onBrowseAllTagsApply}
									onRequestClose={() => setShowingBrowseAllTags(false)}
								/>
							</>
						) : null}
					</div>
				) : null}
				{savedSearches.length > 0 && (
					<div className={css(styleSheet.sectionContainer)}>
						<div className={css(styleSheet.header)}>Saved Searches</div>
						<div>
							<PeopleCollapsibleSection
								defaultCountToShow={MaxItemsToShowByDefault}
								items={renderSavedSearches()}
								itemHeightPx={31}
								showLoader={searchesLoading}
							/>
						</div>
					</div>
				)}
				<div className={css(styleSheet.sectionContainer)}>
					<div className={css(styleSheet.header)}>Contacts</div>
					<div>
						{selectedContacts.length === 0 ? (
							<div className={css(styleSheet.smallGray)} style={{ maxWidth: 220 }}>
								{emailComposer?.emailMessage?.options.sendEmailFrom === SendEmailFrom.ContactOwner
									? 'Selecting individual contacts while sending on behalf of contact owners is not supported.'
									: 'You can use the search to add individual contacts to your campaign.'}
							</div>
						) : (
							<PeopleCollapsibleSection
								defaultCountToShow={MaxItemsToShowByDefault}
								items={renderContacts()}
								itemHeightPx={28}
							/>
						)}
					</div>
				</div>
				<div className={css(styleSheet.tagsFooter)}>
					<button
						className={css(baseStyleSheet.ctaButton)}
						disabled={!selectedContacts.length && !tags.filter(t => t.selected).length && !selectedSavedSearch}
						onClick={onPreviewRecipientsClick}
					>
						<span>{nextCtaText || 'Next: Preview Recipients'}</span>
					</button>
				</div>
			</div>
		</div>
	);
};

const SearchAsObserver = observer(SearchBase);
export const Search = inject(ImpersonationContextKey)(SearchAsObserver);
